<template>
  <div class="validation-sidebar bg-light rounded p-3">
    <div class="d-flex align-items-center justify-content-between mb-2">
      <label class="section-label mb-0">
        <i class="fas fa-clipboard-check"></i> Validation
      </label>
      <span class="category-chip issue-count-chip" :class="issues.length ? (hasErrors ? 'category-chip-danger' : 'category-chip-warning') : 'category-chip-success'">
        {{ issues.length ? (issues.length + (issues.length === 1 ? ' issue' : ' issues')) : 'All clear' }}
      </span>
    </div>

    <div class="change-history mb-3 pb-2">
      <div class="d-flex align-items-center justify-content-between mb-1">
        <label class="sidebar-group-label mb-0">
          <i class="fas fa-history"></i> Changes ({{ changeHistory.length }})
        </label>
        <b-button size="sm" variant="outline-secondary" class="undo-last-btn"
          :disabled="!changeHistory.length" :title="undoTitle" @click="onUndo">
          <i class="fas fa-undo"></i> Undo last
        </b-button>
      </div>
      <ol class="change-history-list list-unstyled mb-0 small text-muted" v-if="changeHistory.length">
        <li v-for="item in reversedHistory" :key="item.index">{{ item.index }}. {{ item.label }}</li>
      </ol>
      <div v-else class="small text-muted">No changes yet.</div>
    </div>

    <div class="validation-sidebar-scroll">
      <ul class="list-unstyled mb-3" v-if="issues.length">
        <li v-for="issue in issues" :key="issue.stepId + '-' + issue.key + '-' + issue.code"
          class="validation-issue d-flex align-items-start py-2 px-2 mb-1 rounded"
          :class="issue.severity === 'error' ? 'validation-issue-error' : 'validation-issue-warning'"
          tabindex="0" role="button"
          :aria-label="'Jump to ' + issue.stepLabel"
          @click="onJump(issue.stepId)"
          @keyup.enter="onJump(issue.stepId)"
          @keydown.space.prevent="onJump(issue.stepId)">
          <i class="fas mr-2 mt-1" :class="issue.severity === 'error' ? 'fa-ban text-danger' : 'fa-exclamation-triangle text-warning'"></i>
          <div class="flex-grow-1">
            <div class="small mb-1">{{ issue.message }}</div>
            <span class="step-jump-link small font-weight-bold">{{ issue.stepLabel }} ↗</span>
          </div>
        </li>
      </ul>
      <div v-else class="text-muted small mb-3">No validation issues found. This procedure's step flow looks good.</div>

      <div class="validation-legend mb-3">
        <label class="sidebar-group-label mb-2 d-block">Connector Legend</label>
        <div class="d-flex align-items-center mb-1">
          <span class="legend-chip category-chip mr-2">↓ Next Step</span>
          <span class="small text-muted">Sequential</span>
        </div>
        <div class="d-flex align-items-center mb-1">
          <span class="legend-chip category-chip category-chip-linked mr-2">→ Goes to</span>
          <span class="small text-muted">Linked to another step</span>
        </div>
        <div class="d-flex align-items-center mb-1">
          <span class="legend-chip category-chip category-chip-warning mr-2">⚠ Unset</span>
          <span class="small text-muted">Destination not chosen</span>
        </div>
        <div class="d-flex align-items-center mb-1">
          <span class="legend-chip category-chip category-chip-danger mr-2">⚠ Loop</span>
          <span class="small text-muted">Infinite loop detected</span>
        </div>
      </div>

      <div class="sidebar-divider"></div>

      <div class="validation-explainer">
        <label class="sidebar-group-label mb-2 d-block">About Loop Detection</label>
        <p class="small text-muted mb-0">
          Each step either continues to the next step, goes to another step, or ends the procedure.
          A loop happens when following those links eventually leads back to a step you've already visited,
          which would trap responders in an endless cycle. Fix loops and set any missing destinations before saving.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ValidationSidebar',
  props: {
    issues: { type: Array, default: function () { return [] } },
    changeHistory: { type: Array, default: function () { return [] } }
  },
  computed: {
    reversedHistory () {
      var list = this.changeHistory.map(function (entry, i) {
        return { index: i + 1, label: entry.label }
      })
      return list.reverse()
    },
    undoTitle () {
      var len = this.changeHistory.length
      return len ? 'Undo: ' + this.changeHistory[len - 1].label : 'Nothing to undo'
    },
    hasErrors () {
      return this.issues.some(function (i) { return i.severity === 'error' })
    }
  },
  methods: {
    onJump (stepId) {
      this.$emit('jump', stepId)
    },
    onUndo () {
      this.$emit('undo')
    }
  }
}
</script>

<style scoped>
.validation-sidebar {
  position: sticky;
  top: 0.5rem;
  max-height: calc(100vh - 140px);
  display: flex;
  flex-direction: column;
}
.validation-sidebar-scroll {
  overflow-y: auto;
  flex: 1 1 auto;
}
.sidebar-group-label {
  font-size: .75em;
  font-weight: 700;
  text-transform: uppercase;
  color: #9a9a9a;
}
.change-history {
  border-bottom: 1px solid #e9ecef;
}
.change-history-list {
  max-height: 150px;
  overflow-y: auto;
}
.sidebar-divider {
  margin: .5em 0 1em;
  border-top: 1px solid #e9ecef;
}
.validation-issue {
  cursor: pointer;
  background: #fff;
  border: 1px solid #e2e6ea;
  border-left-width: 3px;
}
.validation-issue-error {
  border-left-color: #dc3545;
}
.validation-issue-warning {
  border-left-color: #ffc107;
}
.validation-issue:hover,
.validation-issue:focus {
  background: #f8f9fa;
  outline: none;
  box-shadow: 0 0 0 2px rgba(0, 81, 141, 0.25);
}
.step-jump-link {
  color: #00518d;
}
.category-chip {
  display: inline-block;
  border-radius: 0.5em;
  padding: .1em .6em;
  font-size: .75em;
  font-weight: 600;
  background-color: #f5f5f5;
  color: #666666;
}
.issue-count-chip {
  font-size: .8em;
}
.category-chip-success {
  background-color: #d4edda;
  color: #187f2c;
}
.category-chip-warning {
  background-color: #fdf3e0;
  color: #8c5429;
}
.category-chip-danger {
  background-color: #fae1e3;
  color: #89151c;
}
.category-chip-linked {
  background-color: #e5f2ff;
  color: #00518d;
}
.legend-chip {
  font-weight: 700;
  min-width: 84px;
  text-align: center;
}
</style>
